import {
  GridCellParams,
  GridColDef,
  GridColumnGroupingModel,
} from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { toast } from 'react-hot-toast'

import { BaseTable } from 'Features/Projects/Table/BaseTable.view'

import {
  AllocationRowType,
  AllocationTableInterface,
} from './Allocations.types'
import {
  LineEdits,
  PendingRows,
  useLinePreview,
  useSaveLineSettings,
} from '../LineSettings/lineRecalculation'
import {
  LinePreviewNote,
  toastLineSaved,
  toastLineSaveFailed,
} from '../LineSettings/LinePreviewNote'

const keysToExclude = new Set(['baseSourceSiteCode', 'skuGroupCode', 'id'])

/**
 * Allocation cells that changed, as SKU production edits named by codes:
 * each work-centre column of a row is that SKU group's share on that line.
 */
const allocationEdits = ({ current, before }: PendingRows): LineEdits => ({
  products: current.flatMap((row, i) => {
    const old = before[i] ?? {}
    return Object.keys(row)
      .filter(
        (key) =>
          !keysToExclude.has(key) &&
          !key.startsWith('Total_') &&
          row[key] !== null &&
          row[key] !== undefined &&
          Number(row[key]) !== Number(old[key])
      )
      .map((workcentreCode) => ({
        baseSourceSiteCode: row.baseSourceSiteCode,
        skuGroupCode: row.skuGroupCode,
        workcentreCode,
        workCentreAllocation: Number(row[workcentreCode]),
      }))
  }),
})
const percentageValueFormatter = (value: GridCellParams) => {
  return value !== null && value !== undefined ? `${Number(value) * 100}%` : ''
}

export const AllocationsTable = ({
  data,
  isLoading,
  projectId,
  scenarioId,
  baskets,
}: AllocationTableInterface) => {
  const COLUMN_WIDTH = 100

  const totalColumns = new Set(
    Object.keys(baskets).map((key) => `Total_${key}`)
  )

  // Create column grouping model from baskets

  const [columns, columnGroupingModel]: [
    GridColDef[],
    GridColumnGroupingModel,
  ] = useMemo(() => {
    if (!data || data.length === 0) return [[], []]
    const fixedColumns: GridColDef[] = [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'skuGroupCode',
        headerName: 'SKU Group Code',
        width: 150,
        type: 'string',
        flex: 1,
      },
    ]

    const cgm = Object.entries(baskets).map(([siteCode, workcentres]) => {
      return {
        groupId: siteCode,
        headerName: `Site: ${siteCode}`,
        children: workcentres.map((workcentre) => ({
          field: workcentre,
          headerName: workcentre,
          flex: 1,
        })),
      }
    })

    cgm.forEach((group) => {
      group.children.forEach((child) => {
        fixedColumns.push({
          field: child.field,
          headerName: child.headerName,
          width: COLUMN_WIDTH,
          type: 'number',
          editable: true,
          valueFormatter: percentageValueFormatter,
          flex: 1,
        })
      })
    })

    return [[...fixedColumns], cgm]
  }, [data, baskets])

  // Prepare data with IDs if not already present
  const preparedData = useMemo(() => {
    return data.map((row, index) => ({
      ...row,
      id: row.id || `${row.baseSourceSiteCode}_${row.skuGroupCode}_${index}`,
    }))
  }, [data])

  // Allocations shows no derived columns, so the preview is the summary line:
  // how many SKU and batch rows the change recalculates, on every line.
  const preview = useLinePreview({
    kind: 'products',
    projectId,
    scenarioId,
    data: preparedData,
    toEdits: allocationEdits,
    showInGrid: false,
  })
  const { mutate: save, isPending: isSaving } = useSaveLineSettings(
    projectId,
    scenarioId,
    toastLineSaved,
    toastLineSaveFailed
  )

  const updateTableData = (changedData: Record<string, AllocationRowType>) => {
    const before = new Map(preparedData.map((row) => [row.id, row]))
    const current = Object.values(changedData)
    const { products = [] } = allocationEdits({
      current,
      before: current.map((row) => before.get(row.id) ?? {}),
    })
    if (!products.length) {
      toast('No allocation changes to save')
      return
    }
    save({ products })
  }

  const processPercentageUpdate = (
    row: AllocationRowType
  ): AllocationRowType => {
    for (const [skuGroup, workcentres] of Object.entries(baskets)) {
      row[`Total_${skuGroup}`] = workcentres.reduce((total, workcentre) => {
        const value = `Total_${skuGroup}` === workcentre ? 0 : row[workcentre]
        return (
          total + (value !== null && value !== undefined ? Number(value) : 0)
        )
      }, 0)
    }
    return row
  }

  return (
    <>
      <LinePreviewNote
        summary={preview.summary}
        isPreviewing={preview.isPreviewing}
      />
      <BaseTable
        data={preparedData}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        isUploading={false}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={['baseSourceSiteCode', 'skuGroupCode']}
        isCellEditable={(params: GridCellParams) => {
          const { field } = params
          return (
            params.row[field] !== null &&
            field !== 'baseSourceSiteCode' &&
            field !== 'skuGroupCode' &&
            !totalColumns.has(field)
          )
        }}
        columnGroupingModel={columnGroupingModel}
        processRow={processPercentageUpdate}
        onPendingChange={preview.onPendingChange}
      />
    </>
  )
}
